import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useToasts } from 'react-toast-notifications';
import { Row, Col, Card, CardBody, Table, Input, FormGroup, Label, Button } from 'reactstrap';
import { withTranslation } from 'react-multi-lang';
import Select from 'react-select';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { DionysosService } from '../Services/DionysosService';
import AuthService from '../Services/AuthService';

const Machines = ({ t }) => {
    const [machines, setMachines] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedDesc1, setSelectedDesc1] = useState(null);
    const [selectedDesc2, setSelectedDesc2] = useState(null);
    const { addToast } = useToasts();
    const dionysosService = new DionysosService();
    const authService = new AuthService();

    const fetchMachines = useCallback(() => {
        const wh = authService.getConfig()?.whCode;
        dionysosService.getMachines(wh)
            .then(data => {
                setMachines(data || []);
            })
            .catch(err => {
                addToast(t('machines.errorLoading'), { appearance: 'error', autoDismiss: true });
            });
    }, [addToast, t]);

    useEffect(() => {
        fetchMachines();

        const handleWhChange = () => {
            fetchMachines();
        };

        window.addEventListener('warehouseChanged', handleWhChange);
        window.addEventListener('storage', handleWhChange);
        return () => {
            window.removeEventListener('warehouseChanged', handleWhChange);
            window.removeEventListener('storage', handleWhChange);
        };
    }, [fetchMachines]);

    const desc1Options = useMemo(() => {
        const unique = [...new Set(machines.map(m => m.Code1).filter(Boolean))];
        return [{ value: null, label: t('machines.all') }, ...unique.sort().map(val => ({ value: val, label: val }))];
    }, [machines, t]);

    const desc2Options = useMemo(() => {
        const unique = [...new Set(machines.map(m => m.Code2 || m.fDimension2Code).filter(Boolean))];
        return [{ value: null, label: t('machines.all') }, ...unique.sort().map(val => ({ value: val, label: val }))];
    }, [machines, t]);

    const filteredMachines = useMemo(() => {
        return machines.filter(m => {
            const matchesSearch = !searchText || 
                (m.Code && m.Code.toLowerCase().includes(searchText.toLowerCase())) ||
                (m.Description && m.Description.toLowerCase().includes(searchText.toLowerCase()));
            
            const matchesDesc1 = !selectedDesc1 || !selectedDesc1.value || m.Code1 === selectedDesc1.value;
            const matchesDesc2 = !selectedDesc2 || !selectedDesc2.value || m.Code2 === selectedDesc2.value || m.fDimension2Code === selectedDesc2.value;

            return matchesSearch && matchesDesc1 && matchesDesc2;
        });
    }, [machines, searchText, selectedDesc1, selectedDesc2]);

    const printLabel = (machine) => {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(machine.Code)}`;
        
        const printWindow = window.open('', '_blank', 'width=600,height=700');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Print Label - ${machine.Code}</title>
                    <style>
                        @page {
                            margin: 0;
                        }
                        body {
                            margin: 0;
                            padding: 0;
                            font-family: Arial, sans-serif;
                            background-color: #f0f0f0;
                        }
                        .no-print {
                            padding: 20px;
                            text-align: center;
                            background-color: white;
                            border-bottom: 1px solid #ddd;
                        }
                        .btn-print {
                            padding: 10px 25px;
                            font-size: 16px;
                            font-weight: bold;
                            background-color: #007bff;
                            color: white;
                            border: none;
                            border-radius: 5px;
                            cursor: pointer;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        .btn-print:hover {
                            background-color: #0056b3;
                        }
                        .label-container {
                            background-color: white;
                            margin: 20px auto;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            text-align: center;
                            box-shadow: 0 0 10px rgba(0,0,0,0.1);
                            padding: 10mm;
                        }
                        .code {
                            font-size: 24pt;
                            font-weight: bold;
                            margin-bottom: 5mm;
                        }
                        .description {
                            font-size: 16pt;
                            margin-bottom: 5mm;
                            padding: 0 5mm;
                        }
                        .qr-code {
                            width: 35mm;
                            height: auto;
                            margin-bottom: 20mm;
                        }
                        hr {
                            width: 100%;
                            border: 0;
                            border-top: 1px solid black;
                            margin: 0;
                        }
                        @media print {
                            body {
                                background-color: transparent;
                            }
                            .no-print {
                                display: none;
                            }
                            .label-container {
                                margin: 0;
                                box-shadow: none;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="no-print">
                        <button class="btn-print" onclick="window.print()">${t('machines.printLabel')}</button>
                    </div>
                    <div class="label-container">
                        <div class="code">${machine.Code}</div>
                        <div class="description">${machine.Description}</div>
                        <img class="qr-code" src="${qrUrl}" />
                        <hr />
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <Card style={{ padding: '20px' }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>{t('machines.title')}</h3>
                    <ReactHTMLTableToExcel
                        id="machinesExcelButton"
                        className="btn btn-success"
                        table="machinesTable"
                        filename="machines"
                        sheet="machines"
                        buttonText="Excel"
                    />
                </div>
                <Row className="mb-4">
                    <Col md="4">
                        <FormGroup>
                            <Label>{t('machines.search')}</Label>
                            <Input 
                                type="text" 
                                value={searchText} 
                                onChange={e => setSearchText(e.target.value)} 
                                placeholder={t('machines.search')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label>{t('machines.description1')}</Label>
                            <Select 
                                options={desc1Options}
                                value={selectedDesc1}
                                onChange={setSelectedDesc1}
                                placeholder={t('machines.description1')}
                                isClearable
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label>{t('machines.description2')}</Label>
                            <Select 
                                options={desc2Options}
                                value={selectedDesc2}
                                onChange={setSelectedDesc2}
                                placeholder={t('machines.description2')}
                                isClearable
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Table striped hover responsive id="machinesTable">
                    <thead>
                        <tr>
                            <th>{t('machines.code')}</th>
                            <th>{t('machines.description')}</th>
                            <th>{t('machines.description1')}</th>
                            <th>{t('machines.description2')}</th>
                            <th>{t('machines.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMachines.map(m => (
                            <tr key={m.Code}>
                                <td>{m.Code}</td>
                                <td>{m.Description}</td>
                                <td>{m.Code1}</td>
                                <td>{m.Code2 || m.fDimension2Code}</td>
                                <td>
                                    <Button color="primary" onClick={() => printLabel(m)}>
                                        {t('machines.printLabel')}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </CardBody>
        </Card>
    );
};

export default withTranslation(Machines);
